import { AnimatePresence } from 'framer-motion';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { Layout } from '../../Layout';
import { RouteManager } from '../../RouteManager';
import { DeviceSettingsDialog } from '../devices/settings/DeviceSettingsDialog';

import { useAuth } from '../../store/AuthStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useQuery } from '../../hooks/useQuery';
import { useLayoutContextState } from '../../LayoutContext';
import { useAdvancedFlowsLoading } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useDevicesLoading } from '../../store/devices/useDevices.ts';
import { useFlowsLoading } from '../../store/flow/useFlows.ts';
import { useMoodsLoading } from '../../store/moods/useMoods.ts';
import { useSelectedHomeyFeatures } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useUsersLoading } from '../../store/users/useUsers.ts';

import styles from './HomePage.module.css';

import { Scroll } from '../../components/common/Scroll';
import { Heading1 } from '../../components/common/text/Heading1';
import { Heading3Bold } from '../../components/common/text/Heading3Bold';
import { Controls } from '../../components/controls/Controls';
import { PairingDelegator } from '../../components/pairing/PairingDelegator';
import { PremiumRequiredDialog } from '../../components/premium/PremiumRequiredDialog';

import { FavoriteDevices } from './FavoriteDevices';
import { FavoriteFlows } from './FavoriteFlows';
import { FavoriteMoods } from './FavoriteMoods';
import { Users } from './Users';
import { Weather } from './Weather';

export function HomePage() {
  const { i18n } = useI18n();
  const location = useLocation();

  const auth = useAuth();
  const features = useSelectedHomeyFeatures();
  const isDevicesLoading = useDevicesLoading();
  const isMoodsLoading = useMoodsLoading();
  const isFlowsLoading = useFlowsLoading();
  const isAdvancedFlowsLoading = useAdvancedFlowsLoading();
  const isUsersLoading = useUsersLoading();
  const { userMe } = useUserMe();

  const { layoutContextState } = useLayoutContextState();

  const deviceMatch = useRouteMatch(`${import.meta.env.BASE_URL}homeys/:homeyId/:deviceId`);
  const deviceId = deviceMatch?.params?.deviceId ?? null;

  const params = useQuery();
  const dialogType = params.get('dialog');
  const dialogResourceKey = params.get('key');

  const isSiblingLoading =
    isDevicesLoading ||
    isMoodsLoading ||
    isFlowsLoading ||
    isAdvancedFlowsLoading ||
    isUsersLoading;

  const message = getGreeting({ i18n, auth, userMe });

  return (
    <Layout
      isRightOpen={deviceId != null}
      content={
        <Scroll
          style={{
            '--scrollbar-thumb-background': layoutContextState.scrollbarThumbBackgroundMV,
          }}
        >
          <section className={styles.root}>
            <Heading1>{message}</Heading1>

            <Weather />

            <section className={styles.section}>
              <Users
                isSiblingLoading={isSiblingLoading}
                onUserPress={(event, user) => {
                  RouteManager.toUserPresence(user.id);
                }}
              />
            </section>

            <section className={styles.section}>
              <Heading3Bold className={styles.heading}>
                {i18n.messageFormatter(`home.favoriteDevices`)}
              </Heading3Bold>

              <FavoriteDevices selectedDeviceId={deviceId} isSiblingLoading={isSiblingLoading} />
            </section>

            {features?.moods.hasMoods === true && userMe?.properties.favoriteMoods?.length > 0 && (
              <section className={styles.section}>
                <Heading3Bold className={styles.heading}>
                  {i18n.messageFormatter(`home.favoriteMoods`)}
                </Heading3Bold>

                <FavoriteMoods isSiblingLoading={isSiblingLoading} />
              </section>
            )}

            <section className={styles.section}>
              <Heading3Bold className={styles.heading}>
                {i18n.messageFormatter(`home.favoriteFlows`)}
              </Heading3Bold>

              <FavoriteFlows isSiblingLoading={isSiblingLoading} />
            </section>
          </section>
        </Scroll>
      }
      right={<Controls deviceId={deviceId} onClosePress={() => RouteManager.toHome()} />}
    >
      <AnimatePresence>
        {dialogType === RouteManager.dialogRoute.deviceSettings && (
          <DeviceSettingsDialog deviceId={dialogResourceKey} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {location.state?.pairDialog != null && (
          <PairingDelegator state={location.state.pairDialog} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {location.state?.premiumRequiredDialog === true && <PremiumRequiredDialog />}
      </AnimatePresence>
    </Layout>
  );
}

function getGreeting({ i18n, auth, userMe }) {
  const now = new Date();
  const hours = now.getHours();

  let message;
  let firstname = auth.cloudUser?.firstname ?? userMe?.localUsername;

  if (hours <= 4) {
    message = i18n.messageFormatter(`greeting.night`, {
      name: firstname,
    });
  } else if (hours < 12) {
    message = i18n.messageFormatter(`greeting.morning`, {
      name: firstname,
    });
  } else if (hours < 18) {
    message = i18n.messageFormatter(`greeting.afternoon`, {
      name: firstname,
    });
  } else {
    message = i18n.messageFormatter(`greeting.evening`, {
      name: firstname,
    });
  }

  return `${message}!`;
}

export default HomePage;
